import { Player } from "./player";
import { Swarm } from "./swarm";
import { Bullet } from "./bullet";
import { Bunker } from "./bunker";
import { CONFIG, GameConfig } from "./config";
import { ControlsLike, GameSnapshot, GameState } from "./types";
import { rectsIntersect } from "./utils";
import { rowBunkerSeeds } from "./layouts/grid";

export class Game {
  state: GameState = GameState.Ready;

  private readonly cfg: GameConfig;
  private player: Player;
  private swarm: Swarm;
  private bullets: Bullet[] = [];
  private bunkers: Bunker[] = [];
  private score = 0;
  private lives: number;
  private level = 1;
  private restartTimer = 0;
  private hudEnabled: boolean;
  private overlayEnabled: boolean;

  constructor(cfg: GameConfig = CONFIG) {
    this.cfg = cfg;
    this.lives = cfg.player.startLives;
    this.player = new Player(cfg);
    this.swarm = new Swarm(this.level, cfg);
    this.bunkers = this.buildBunkers();
    this.hudEnabled = cfg.hud;
    this.overlayEnabled = cfg.overlay;
  }

  /** Shows or hides the HUD and title card without rebuilding the game. */
  setChrome(options: { hud?: boolean; overlay?: boolean }): void {
    if (options.hud !== undefined) this.hudEnabled = options.hud;
    if (options.overlay !== undefined) this.overlayEnabled = options.overlay;
  }

  private buildBunkers(): Bunker[] {
    const seeds = (this.cfg.layout?.bunkers ?? rowBunkerSeeds)(this.cfg);
    return seeds.map(
      (s) => new Bunker(s.x, s.y, s.shape, s.blockSize, this.cfg.colors.bunker)
    );
  }

  restart(): void {
    this.score = 0;
    this.lives = this.cfg.player.startLives;
    this.level = 1;
    this.startLevel();
  }

  private startLevel(): void {
    this.player.reset();
    this.swarm = new Swarm(this.level, this.cfg);
    this.bunkers = this.buildBunkers();
    this.bullets = [];
    this.restartTimer = 0;
    this.state = GameState.Playing;
  }

  private advanceLevel(): void {
    if (this.level >= this.cfg.game.maxLevels) {
      this.state = GameState.Win;
      return;
    }
    this.level += 1;
    this.startLevel();
  }

  update(dt: number, controls: ControlsLike): void {
    if (this.state === GameState.Ready) {
      if (controls.fire) this.state = GameState.Playing;
      return;
    }
    if (this.state !== GameState.Playing) {
      // The hero runs as an attract loop: it reforms the word on its own
      // rather than waiting for a keypress that will never come.
      const delay = this.cfg.autoRestartDelay;
      if (delay !== null) {
        this.restartTimer += dt;
        if (this.restartTimer >= delay) this.restart();
        return;
      }
      if (controls.fire) this.restart();
      return;
    }

    const dir = (controls.left ? -1 : 0) + (controls.right ? 1 : 0);
    this.player.update(dt, dir);
    if (
      controls.fire &&
      this.player.canFire() &&
      this.countPlayerBullets() < this.cfg.bullet.maxPlayerBullets
    ) {
      const pos = this.player.fire();
      this.bullets.push(new Bullet(pos.x, pos.y, "player", this.cfg));
    }

    if (this.swarm.update(dt, this.cfg.player.y)) {
      this.lives = 0;
      this.state = GameState.GameOver;
      return;
    }

    const shot = this.swarm.tryFire(dt);
    if (shot && this.countInvaderBullets() < this.cfg.bullet.maxInvaderBullets) {
      this.bullets.push(shot);
    }

    for (const b of this.bullets) b.update(dt);
    this.resolveCollisions();
    this.bullets = this.bullets.filter((b) => !b.dead);

    if (this.swarm.isCleared) this.advanceLevel();
  }

  /** A read-only view for the autopilot. Allocates, so call it sparingly. */
  snapshot(): GameSnapshot {
    return {
      player: this.player.rect,
      invaders: this.swarm.invaders.filter((i) => i.alive).map((i) => i.rect),
      invaderBullets: this.bullets
        .filter((b) => b.owner === "invader" && !b.dead)
        .map((b) => b.rect),
      canvasWidth: this.cfg.canvas.width,
    };
  }

  private countPlayerBullets(): number {
    return this.bullets.filter((b) => b.owner === "player" && !b.dead).length;
  }

  private countInvaderBullets(): number {
    return this.bullets.filter((b) => b.owner === "invader" && !b.dead).length;
  }

  private resolveCollisions(): void {
    for (const b of this.bullets) {
      if (b.dead) continue;

      for (const bunker of this.bunkers) {
        if (bunker.hit(b.rect)) {
          b.dead = true;
          break;
        }
      }
      if (b.dead) continue;

      if (b.owner === "player") {
        for (const inv of this.swarm.invaders) {
          if (inv.alive && rectsIntersect(inv.rect, b.rect)) {
            inv.alive = false;
            this.score += inv.points;
            b.dead = true;
            break;
          }
        }
      } else if (rectsIntersect(this.player.rect, b.rect)) {
        b.dead = true;
        this.loseLife();
      }
    }
  }

  private loseLife(): void {
    this.lives -= 1;
    if (this.lives <= 0) {
      this.lives = 0;
      this.state = GameState.GameOver;
    } else {
      this.player.reset();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = this.cfg.colors.background;
    ctx.fillRect(0, 0, this.cfg.canvas.width, this.cfg.canvas.height);

    for (const bunker of this.bunkers) bunker.draw(ctx);
    this.swarm.draw(ctx);
    for (const b of this.bullets) b.draw(ctx);
    if (this.state !== GameState.GameOver) this.player.draw(ctx);

    if (this.hudEnabled) this.drawHUD(ctx);
    if (this.overlayEnabled) this.drawOverlay(ctx);
  }

  private drawHUD(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = this.cfg.colors.text;
    ctx.font = "16px monospace";
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.fillText(`SCORE ${this.score}`, 16, 26);
    ctx.textAlign = "center";
    ctx.fillText(`LEVEL ${this.level}`, this.cfg.canvas.width / 2, 26);
    ctx.textAlign = "right";
    ctx.fillText(`LIVES ${this.lives}`, this.cfg.canvas.width - 16, 26);
  }

  private drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.state === GameState.Playing) return;

    ctx.fillStyle = this.cfg.colors.overlay;
    ctx.fillRect(0, 0, this.cfg.canvas.width, this.cfg.canvas.height);

    let title = "";
    let subtitle = "";
    switch (this.state) {
      case GameState.Ready:
        title = "SPACE INVADERS";
        subtitle = "Press FIRE / Space to start";
        break;
      case GameState.Win:
        title = "YOU WIN!";
        subtitle = `Score ${this.score} — Press FIRE / Space to play again`;
        break;
      case GameState.GameOver:
        title = "GAME OVER";
        subtitle = `Score ${this.score} — Press FIRE / Space to retry`;
        break;
    }

    ctx.fillStyle = this.cfg.colors.text;
    ctx.textAlign = "center";
    ctx.font = "40px monospace";
    ctx.fillText(
      title,
      this.cfg.canvas.width / 2,
      this.cfg.canvas.height / 2 - 20
    );
    ctx.font = "18px monospace";
    ctx.fillText(
      subtitle,
      this.cfg.canvas.width / 2,
      this.cfg.canvas.height / 2 + 24
    );
  }
}
